import { DateMode } from "./types";
import {
  MaskedDateRangeInput,
  MaskedDateRangeInputProps,
  MaskedDateRangeInputRef,
} from "./internal/MaskedDateRangeInput";
import { useFocusWithin } from "../../hooks/useFocusWithin";
import { useDateFieldRangeConversion } from "./internal/useDateFieldRangeConversion";
import { RefObject, useMemo, useRef, useState } from "react";
import { useMergeRefs } from "../../hooks";
import { usePopoverSupport } from "../../internal/hooks/usePopoverSupport";
import {
  DateFieldRangeStateChange,
  useDateFieldRangeState,
} from "./internal/useDateFieldRangeState";
import { useDateFieldOrchestration } from "./internal/useDateFieldOrchestration";

import { validateDate } from "./internal/utils";
import { Popover } from "../Popover";
import { DateFieldRangeCalendar } from "./internal/DateFieldRangeCalendar";
import { DateTime } from "luxon";
import { usePopoverCloseDelayWorkaround } from "../../internal";

export type DateFieldRangeChange = {
  /**
   * The start date in ISO 8601 format.
   * @example "2025-03-22"
   */
  startDate: string | null;
  /**
   * The end date in ISO 8601 format.
   * @example "2025-07-02"
   */
  endDate: string | null;
  /**
   * Whether the input field contains two parsable dates.
   * This is not the same as the date range being valid.
   *
   */
  isInputValid: boolean;
  /**
   * Whether the input field is empty.
   */
  isInputEmpty: boolean;
  /**
   * Whether the start and end dates are valid according to the constraints.
   * Constraints include:
   * - Required
   * - Unavailable dates
   * - Unavailable days of the week
   * - Min date
   * - Max date
   */
  isDateRangeValid: boolean;
};

export type DateFieldRangeChangeHandler = (
  change: DateFieldRangeChange,
) => void;

export type DateFieldRangeValue = {
  startDate: string | null;
  endDate: string | null;
} | null;

export type DateFieldRangeProps = Omit<
  MaskedDateRangeInputProps,
  | "onChange"
  | "value"
  | "autoComplete"
  | "onClick"
  | "onKeyDown"
  | "ref"
  | "required"
  | "startDate"
  | "endDate"
  | "defaultValue"
  | "onFocus"
  | "onBlur"
> & {
  value?: DateFieldRangeValue;
  defaultValue?: DateFieldRangeValue;
  onChange?: DateFieldRangeChangeHandler;
  mode?: Extract<DateMode, "mm/dd/yyyy" | "dd/mm/yyyy">;
  disableHint?: boolean;
  disableCalendar?: boolean;
  unavailable?: {
    dates?: string[];
    daysOfWeek?: (1 | 2 | 3 | 4 | 5 | 6 | 7)[];
  };
  minDate?: string;
  maxDate?: string;
  required?: boolean;
  onFocus?: (event: FocusEvent) => void;
  onBlur?: (event: FocusEvent) => void;
};

export const DateFieldRange = ({
  onFocus,
  onBlur,
  disableCalendar,
  required,
  mode,
  value: valueProp,
  defaultValue: defaultValueProp,
  minDate: minDateProp,
  maxDate: maxDateProp,
  unavailable: unavailableProp,
  onChange: onChangeProp,
  ...rest
}: DateFieldRangeProps) => {
  const { ref: wrapperRef } = useFocusWithin({
    onBlur: onBlur,
    onFocus: onFocus,
  });

  const { value, defaultValue, minDate, maxDate, unavailable, onChange } =
    useDateFieldRangeConversion({
      value: valueProp,
      defaultValue: defaultValueProp,
      minDate: minDateProp,
      maxDate: maxDateProp,
      unavailable: unavailableProp,
      onChange: onChangeProp,
    });

  const inputRef = useRef<MaskedDateRangeInputRef>(null);
  const [popoverTriggerRef, setPopoverTriggerRef] =
    useState<RefObject<HTMLInputElement>>();
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const combinedRef = useMergeRefs([popoverTriggerRef, inputRef]);
  const popoverSupported = usePopoverSupport();

  const handleChange = (change: DateFieldRangeStateChange) => {
    const sharedConstraints = {
      unavailable,
      minDate: minDate ?? undefined,
      maxDate: maxDate ?? undefined,
    };

    onChange?.({
      startDate: change.startDate?.startOf("day") ?? null,
      endDate: change.endDate?.startOf("day") ?? null,
      isInputValid: change.isInputValid,
      isInputEmpty: change.isInputEmpty,
      isDateRangeValid: validateDateRange({
        required: required,
        startDate: change.startDate?.startOf("day") ?? null,
        endDate: change.endDate?.startOf("day") ?? null,
        startDateConstraints: sharedConstraints,
        endDateConstraints: sharedConstraints,
      }),
    });
  };

  const { startDate, endDate, handleInputChange, handleCalendarSelection } =
    useDateFieldRangeState({
      valueProp: value,
      defaultValueProp: defaultValue,
      inputRef,
      onChange: handleChange,
    });

  const {
    calendarOpen,
    setCalendarOpen,
    handleCalendarKeyDown,
    handleInputKeyDown,
  } = useDateFieldOrchestration({
    inputRef,
    calendarDefaultOpen: false,
    popoverContentRef,
    disableCalendar,
  });

  const shouldShowCalendar = usePopoverCloseDelayWorkaround(calendarOpen);

  const currentValidity = useMemo(() => {
    return validateDateRange({
      required,
      startDate,
      endDate,
      startDateConstraints: {
        unavailable,
        minDate: minDate ?? undefined,
        maxDate: maxDate ?? undefined,
      },
      endDateConstraints: {
        unavailable,
        minDate: minDate ?? undefined,
        maxDate: maxDate ?? undefined,
      },
    });
  }, [required, startDate, endDate, minDate, maxDate, unavailable]);

  const justTheField = (
    <MaskedDateRangeInput
      {...rest}
      mode={mode}
      ref={combinedRef}
      startDate={startDate ?? null}
      endDate={endDate ?? null}
      onChange={handleInputChange}
      disableHint={rest.disableHint}
      onKeyDown={handleInputKeyDown}
      onClick={() => setCalendarOpen(true)}
      required={required}
      autoComplete="off"
      data-date-range-valid={currentValidity}
    />
  );

  if (disableCalendar) {
    return justTheField;
  }
  if (!popoverSupported) {
    return justTheField;
  }
  return (
    <div ref={wrapperRef}>
      <Popover
        open={calendarOpen}
        modal
        placement="bottom-start"
        disableFlipFallback
        disableTriggerFocus
        onClose={() => setCalendarOpen(false)}
        disableAutoUpdate
        onOutsidePress={() => setCalendarOpen(false)}
      >
        <Popover.Trigger>
          {({ ref: iRef }) => {
            setPopoverTriggerRef(iRef as RefObject<HTMLInputElement>);
            return justTheField;
          }}
        </Popover.Trigger>
        <Popover.Content ref={popoverContentRef} data-testid="calendar-popover">
          {shouldShowCalendar && (
            <DateFieldRangeCalendar
              startDate={startDate}
              endDate={endDate}
              onKeyDown={handleCalendarKeyDown}
              onSelection={handleCalendarSelection}
              minDate={minDate}
              maxDate={maxDate}
              unavailable={unavailable}
            />
          )}
        </Popover.Content>
      </Popover>
    </div>
  );
};

DateFieldRange.displayName = "DateFieldRange";

type DateConstraints = {
  unavailable?: {
    dates?: DateTime[];
    daysOfWeek?: number[];
  };
  minDate?: DateTime;
  maxDate?: DateTime;
};

function validateDateRange({
  required,
  startDate,
  endDate,
  startDateConstraints,
  endDateConstraints,
}: {
  required?: boolean;
  startDate: DateTime | null;
  endDate: DateTime | null;
  startDateConstraints: DateConstraints;
  endDateConstraints: DateConstraints;
}) {
  if (!required && !startDate && !endDate) return true;
  return (
    validateDate({
      date: startDate,
      constraints: { ...startDateConstraints, required: true },
    }) &&
    validateDate({
      date: endDate,
      constraints: { ...endDateConstraints, required: true },
    }) &&
    (!startDate || !endDate || startDate <= endDate)
  );
}
